<script setup lang="ts">
import { yieldToMain } from '@@/bits/global'
import { defineAsyncComponent, vDel } from '@@/bits/vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useAutoScrollElement } from '@@/pragmatic-drag-and-drop/useAutoScrollElement'
import type { Id, WallGroupPost } from '@@/types'
import SurfacePostsDraggablePad from '@@/vuecomponents/SurfacePostsDraggablePad.vue'
import SurfacePostsGridSection, {
  SURFACE_POSTS_GRID_POST_DRAGGABLE_TYPE,
} from '@@/vuecomponents/SurfacePostsGridSection.vue'
import { useScrollToNewestPost } from '@@/vuecomposables/surface_scroll_to_newest_post'
import { storeToRefs } from 'pinia'
import type { ComponentPublicInstance, Ref } from 'vue'
import { computed, onMounted, ref } from 'vue'

const SurfaceAddSectionButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddSectionButton.vue'))

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const surfacePostsStore = useSurfacePostsStore()
const { backgroundLuminance, dir, isReadOnly, isScreenshotMode, isFrozen } = storeToRefs(useSurfaceStore())
const { isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { sortedGroups, isGroupBySection, isGroupBySingleSelect } = storeToRefs(useSurfaceWishArrangementStore())

const { shouldDisplayGroup } = surfacePostsStore

const rootElement: Ref<HTMLDivElement | null> = ref(null)

function assignRootElementRef(v: ComponentPublicInstance | null): void {
  rootElement.value = v?.$el as HTMLDivElement | null
}

useScrollToNewestPost(rootElement)

const postGroupHeaderHeights = ref<Record<WallGroupPost['id'], number>>({})

const maxPostGroupHeaderHeight = computed(() => Math.max(...Object.values(postGroupHeaderHeights.value)))

function updatePostGroupHeaderHeights(groupId: WallGroupPost['id'], height: number): void {
  postGroupHeaderHeights.value = {
    ...postGroupHeaderHeights.value,
    [groupId]: height,
  }
}

function removePostGroupHeaderHeight(groupId: WallGroupPost['id']): void {
  vDel(postGroupHeaderHeights.value, groupId)
}

// #region Horizontal auto scroll for posts
useAutoScrollElement({
  scrollContainerElementRef: rootElement,
  type: SURFACE_POSTS_GRID_POST_DRAGGABLE_TYPE,
})
// #endregion

const isReadyToMountGridSections = ref(false)
const isReadyToMountAddSectionButton = ref(false)
onMounted(async () => {
  // We yield execution back to the browser before mounting the internal components to avoid blocking the main thread.
  // This is to avoid high TBT (Total Blocking Time), which contributes negatively to the Core Web Vitals.
  await yieldToMain()
  isReadyToMountGridSections.value = true

  await yieldToMain()
  isReadyToMountAddSectionButton.value = true
})
</script>

<template>
  <SurfacePostsDraggablePad
    :ref="assignRootElementRef"
    :dir="dir"
    :class="[
      'flex',
      'flex-row',
      'space-s-4',
      // Removing overflow classes increase document.documentElement.scrollWidth and allows full screenshot with full_width param
      !isScreenshotMode && 'overflow-y-hidden overflow-x-auto',
      {
        'scrollbar-photo': backgroundLuminance === 'light',
        'scrollbar-photo-dark': backgroundLuminance === 'dark',
      },
      'mx-auto',
      'box-border',
      'w-full',
      'px-3 tablet-portrait:px-4',
      'overflow-y-scroll',
    ]"
  >
    <template v-if="isReadyToMountGridSections">
      <SurfacePostsGridSection
        v-for="group in sortedGroups"
        v-show="shouldDisplayGroup(group)"
        :key="group.id"
        :group="group"
        :reaction-ids-by-post-id="reactionIdsByPostId"
        :comment-ids-by-post-id="commentIdsByPostId"
        :post-group-header-heights="postGroupHeaderHeights"
        :max-post-group-header-height="maxPostGroupHeaderHeight"
        @update-post-group-header-heights="updatePostGroupHeaderHeights"
        @remove-post-group-header-height="removePostGroupHeaderHeight"
      />
    </template>

    <div
      v-if="
        (isGroupBySection || isGroupBySingleSelect) &&
        canIModerate &&
        !isReadOnly &&
        !isFrozen &&
        !isDemoPadletPanelDesktop &&
        isReadyToMountAddSectionButton
      "
      key="surface-add-section-button"
      class="flex-none flex flex-col items-start justify-end me-auto w-fit mt-2"
      :style="{ height: `${maxPostGroupHeaderHeight}px`, marginInlineEnd: 'auto' }"
    >
      <SurfaceAddSectionButton class="mb-11 tablet-portrait:mb-12" />
    </div>
  </SurfacePostsDraggablePad>
</template>
